import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, CheckCircle2, Briefcase } from "lucide-react";
import { api } from "@/lib/api";

const PARTNER_TYPES = [
  { value: "agent_partner", label: "Real Estate Agent / Broker", orgLabel: "Brokerage / Team" },
  { value: "probate_attorney", label: "Probate / Estate Attorney", orgLabel: "Firm" },
  { value: "property_manager", label: "Property Manager", orgLabel: "Management Company" },
  { value: "contractor", label: "Contractor / Service Pro", orgLabel: "Company" },
];

export default function PartnerForm({ defaultPartnerType = "agent_partner" }) {
  const [partnerType, setPartnerType] = useState(defaultPartnerType);
  useEffect(() => setPartnerType(defaultPartnerType), [defaultPartnerType]);

  const [form, setForm] = useState({
    full_name: "",
    brokerage: "",
    phone: "",
    email: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const orgLabel =
    PARTNER_TYPES.find((p) => p.value === partnerType)?.orgLabel || "Company";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        full_name: form.full_name,
        phone: form.phone,
        email: form.email || null,
        brokerage: form.brokerage || null,
        message: form.message || null,
        situation: partnerType,
        source: "partners_section",
      };
      await api.post("/leads", payload);
      setDone(true);
      toast.success("Thanks! Our partnerships team will reach out within one business day.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="partner-form-success"
        className="rounded-lg bg-white border border-[#E5E3DB] p-8 text-center"
      >
        <CheckCircle2 className="h-10 w-10 text-[#2C5545] mx-auto mb-4" />
        <h3 className="font-display text-2xl text-[#0B1B3D] mb-2">
          Welcome aboard, partner.
        </h3>
        <p className="text-[#64748B] text-sm leading-relaxed">
          Our partnerships team will reach out within one business day. For urgent matters, text <span className="font-medium text-[#0B1B3D]">706-506-3860</span>.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="partner-form"
      className="rounded-lg bg-white border border-[#E5E3DB] p-7 lg:p-8 shadow-[0_20px_60px_rgba(11,27,61,0.10)]"
    >
      <div className="flex items-center gap-2 mb-1">
        <Briefcase className="h-4 w-4 text-[#2C5545]" />
        <span className="text-[11px] uppercase tracking-[0.22em] text-[#2C5545] font-medium">
          For Industry Partners
        </span>
      </div>
      <h3 className="font-display text-2xl lg:text-3xl text-[#0B1B3D] mb-1">
        Become a Clear Path Partner
      </h3>
      <p className="text-sm text-[#64748B] mb-6">
        Tell us a bit about you. We'll show you how working together pays off.
      </p>

      <div className="grid grid-cols-1 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
            I'm a... *
          </Label>
          <Select value={partnerType} onValueChange={setPartnerType}>
            <SelectTrigger className="mt-1.5 h-11" data-testid="pf-partner-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PARTNER_TYPES.map((p) => (
                <SelectItem key={p.value} value={p.value} data-testid={`pf-type-${p.value}`}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="pf-name" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
            Full Name *
          </Label>
          <Input
            id="pf-name"
            data-testid="pf-full-name"
            value={form.full_name}
            onChange={(e) => upd("full_name", e.target.value)}
            placeholder="Alex Carter"
            className="mt-1.5 h-11"
            required
          />
        </div>

        <div>
          <Label htmlFor="pf-org" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
            {orgLabel}
          </Label>
          <Input
            id="pf-org"
            data-testid="pf-brokerage"
            value={form.brokerage}
            onChange={(e) => upd("brokerage", e.target.value)}
            placeholder={orgLabel === "Firm" ? "Carter & Associates" : "Your company"}
            className="mt-1.5 h-11"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="pf-phone" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
              Phone *
            </Label>
            <Input
              id="pf-phone"
              data-testid="pf-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => upd("phone", e.target.value)}
              placeholder="(555) 555-0110"
              className="mt-1.5 h-11"
              required
            />
          </div>
          <div>
            <Label htmlFor="pf-email" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
              Email
            </Label>
            <Input
              id="pf-email"
              data-testid="pf-email"
              type="email"
              value={form.email}
              onChange={(e) => upd("email", e.target.value)}
              placeholder="alex@company.com"
              className="mt-1.5 h-11"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="pf-msg" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
            Tell us how we can help
          </Label>
          <Textarea
            id="pf-msg"
            data-testid="pf-message"
            value={form.message}
            onChange={(e) => upd("message", e.target.value)}
            placeholder="A stale listing, a probate case, vacancies, distressed property..."
            className="mt-1.5 min-h-[90px]"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="pf-submit"
          className="mt-2 inline-flex items-center justify-center gap-2 w-full px-8 py-4 rounded-lg bg-[#0B1B3D] hover:bg-[#1A2F5C] disabled:opacity-60 text-white font-semibold transition-all shadow-md hover:shadow-lg"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            "Apply to Partner With Us"
          )}
        </button>

        <p className="text-[11px] text-[#64748B] text-center leading-relaxed mt-1">
          We respond within one business day. No long-term contracts, no fees to apply.
        </p>
      </div>
    </form>
  );
}
